import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTitle } from "../useTitle";
import { ApiError, api, setCsrf } from "../api";
import { Notice } from "../components/Look";

/**
 * The sign-in gate.
 *
 * TWO SHAPES, ONE FORM. An instance with named accounts asks for a username;
 * one that has none is still on its bootstrap password and asks for the
 * password alone. Which of the two is a fact about the SERVER (`namedAccounts`
 * from /me), never a guess: showing a username box on a bootstrap instance
 * would have people typing a name that is then ignored, and hiding it after
 * accounts exist refuses every correct password with no explanation.
 *
 * When no password is configured at all the server answers /me as an
 * authenticated anonymous principal, so App never renders this. If it IS
 * rendered with `passwordRequired` false, something is wrong in a way worth
 * saying out loud rather than showing a box that cannot work.
 */
export default function Login({
  onLoggedIn,
  passwordRequired,
  namedAccounts = false,
}: {
  onLoggedIn: () => void;
  passwordRequired: boolean;
  namedAccounts?: boolean;
}) {
  const { t } = useTranslation();
  useTitle(t("login.title"));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.login(password, namedAccounts ? username.trim() : undefined);
      setCsrf(r.csrf);
      setError("");
      // onLoggedIn re-reads /me, which reports must_change_password; App then
      // renders the change form rather than the app.
      onLoggedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-slate-50 p-4 dark:bg-slate-950">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
      >
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {t("login.heading")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {passwordRequired ? t("login.prompt") : t("login.noPassword")}
        </p>

        {namedAccounts && (
          <label className="mt-4 block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">
              {t("login.username")}
            </span>
            <input
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </label>
        )}

        <label className="mt-4 block text-sm">
          <span className="mb-1 block text-slate-600 dark:text-slate-400">
            {t("login.password")}
          </span>
          <input
            type="password"
            autoFocus={!namedAccounts}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </label>

        {error && <Notice tone="error">{error}</Notice>}

        <button
          type="submit"
          disabled={busy || !password || (namedAccounts && !username.trim())}
          className="btn-primary mt-4 w-full"
        >
          {busy ? t("login.submitting") : t("login.submit")}
        </button>
      </form>
    </div>
  );
}
